/* ─────────────────────────────────────────────────────────────────────────────
 * section-fp-v2-main.js
 * Comportements minimaux de la section "FP v2 — Main" (Figma 2026).
 *
 * RÔLE :
 *   - Galerie : sync thumbnail ↔ stage (no full reload).
 *   - Variant matching côté client : recalcul de la variante courante depuis
 *     les radios d'options, mise à jour prix / stock / disponibilité ATC.
 *   - Quantité : boutons +/-.
 *   - Émission via window.eventBus pour les autres composants du thème :
 *       'fpv2:variant:change' → { sectionId, variant }
 *
 * NON-RÔLE :
 *   - Pas de fetch /products/<handle>.js : on lit le JSON intégré dans la
 *     section (data-fpv2-variants-json), c'est plus rapide et suffisant.
 *   - Pas de gestion ATC : déléguée au custom element <product-form> existant.
 *   - Pas de duplication des swatchs : <product-swatch> existant gère le
 *     hover-preview de couleur ; on ne touche qu'à la sélection.
 *
 * SÉCURITÉ :
 *   - Aucune injection de HTML utilisateur ; on lit uniquement values d'inputs
 *     et on assigne via .textContent ou .toggleAttribute.
 * ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.__fpv2MainInitialized) return;
  window.__fpv2MainInitialized = true;

  /* Format prix — implémentation Shopify standard (même découpage que
   * theme.js), sur le `money_format` de la boutique. La version précédente
   * ignorait le séparateur de milliers et les variantes `..._with_space/
   * apostrophe_separator` : depuis que les prix du CTA et du bloc abonnement
   * sont rendus par JS dès le chargement, un écart de format serait visible
   * sans même toucher au sélecteur de variantes. */
  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (typeof precision !== 'number') precision = 2;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (typeof cents !== 'number' || isNaN(cents)) return '0';
    var parts = (cents / 100.0).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseFloat(cents.replace('.', ''));
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var match = format.match(MONEY_PLACEHOLDER);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(MONEY_PLACEHOLDER, value);
  }

  /** Initialise une instance de section. */
  function initSection(section) {
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return;

    syncFpv2Layout();
    var gallery = initGallery(section);
    initThumbScroll(section);
    initVariantPicker(section, sectionId, gallery);
    initQuantity(section);
    initSubscription(section);
    initAccordions(section);
    initShortDesc(section);
    initScrollLock(section);
  }

  /* ── Description courte : "Lire la suite / Lire moins" ──────────────────
   * Source = description native du produit (product.description) : HTML libre
   * (paragraphes, listes, titres…). On ne peut donc PAS se fier au nombre de
   * <p> : le repli est piloté par la HAUTEUR. Le contenu est enveloppé dans un
   * inner clampé via CSS (.is-collapsed → max-height) ; s'il dépasse, on pose un
   * toggle dont le libellé vient de Liquid (data-more-label / data-less-label,
   * résolus via `| t` → dépendent de la langue de la boutique).
   *
   * Progressive enhancement : sans JS, toute la description reste visible.
   * No-op si le contenu tient déjà dans la hauteur repliée. Idempotent en
   * éditeur de thème (garde data-fpv2-short-desc-ready). */
  function initShortDesc(section) {
    var block = section.querySelector('[data-fpv2-short-desc]');
    if (!block || block.hasAttribute('data-fpv2-short-desc-ready')) return;
    block.setAttribute('data-fpv2-short-desc-ready', '');

    // Enveloppe le HTML de la description dans un inner clampable en hauteur.
    // Le toggle reste hors de cet inner pour ne jamais être masqué au repli.
    var inner = document.createElement('div');
    inner.className = 'fp-v2-short-desc__inner';
    while (block.firstChild) inner.appendChild(block.firstChild);
    block.appendChild(inner);

    // Replie pour mesurer : si le contenu tient dans la hauteur clampée,
    // rien à replier → pas de "Lire la suite".
    block.classList.add('is-collapsed');
    if (inner.scrollHeight <= inner.clientHeight + 4) {
      block.classList.remove('is-collapsed');
      return;
    }

    var moreLabel = block.getAttribute('data-more-label') || 'Lire la suite';
    var lessLabel = block.getAttribute('data-less-label') || 'Lire moins';
    // Card info : repliée, elle garde sa hauteur "1 écran" (= galerie) mais on
    // coupe le flex-grow de la zone scroll → le foot remonte, vide assumé en bas
    // (cf. .is-shortdesc-collapsed dans le CSS). null en mobile/stack : défensif.
    var info = block.closest('.fp-v2-main__info');
    if (info) info.classList.add('is-shortdesc-collapsed');

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'fp-v2-short-desc__toggle';
    toggle.textContent = moreLabel;
    toggle.setAttribute('aria-expanded', 'false');
    // Dernier enfant du bloc (hors inner) : replié il suit la zone clampée,
    // déplié il se cale sous le contenu complet.
    block.appendChild(toggle);

    toggle.addEventListener('click', function () {
      var collapsed = block.classList.toggle('is-collapsed');
      if (info) info.classList.toggle('is-shortdesc-collapsed', collapsed);
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      toggle.textContent = collapsed ? moreLabel : lessLabel;
    });
  }

  /* ── Scroll lock : la page ne scrolle qu'une fois la card info au fond
   * (desktop uniquement, mobile stack vertical) ──────────────────────────
   *
   * Comportement attendu :
   *   - Quand le top de la section atteint le top du viewport (section pin),
   *     toute action de scroll vertical est redirigée vers .fp-v2-main__info
   *     (scroll interne de la card).
   *   - Une fois la card scrollée jusqu'en bas (resp. en haut si on remonte),
   *     le scroll redevient celui de la page → la section sort du viewport
   *     normalement.
   *   - Reste passif quand la section n'est pas pinnée (au-dessus ou
   *     en-dessous du viewport).
   *
   * Caveats :
   *   - Wheel & touch supportés. Clavier (PgDn/Space/flèches) non interceptés
   *     volontairement (a11y : on n'enferme pas un utilisateur clavier).
   *   - macOS trackpad inertia : un seul event wheel "spike" peut être perdu
   *     à la transition lock→unlock, acceptable. */
  function initScrollLock(section) {
    if (window.matchMedia('(max-width: 749px)').matches) return;

    var card = section.querySelector('.fp-v2-main__info');
    if (!card) return;

    function isPinned() {
      var rect = section.getBoundingClientRect();
      // Dès que la section est visible (ne serait-ce qu'une portion), tout
      // scroll est redirigé vers la card. Plus tolérant que le « top atteint »
      // précédent — sinon le verrou ne s'engageait qu'au milieu du scroll
      // principal et la card ne scrollait pas à temps.
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }
    function atBottom() {
      return Math.ceil(card.scrollTop + card.clientHeight) >= card.scrollHeight - 1;
    }
    function atTop() {
      return card.scrollTop <= 0;
    }
    function cardHasOverflow() {
      return card.scrollHeight - card.clientHeight > 1;
    }

    /* Wheel : on intercepte tant que la card a encore du scroll dans le
       sens demandé. Sinon on laisse passer pour que la page reprenne. */
    window.addEventListener('wheel', function (e) {
      if (!isPinned() || !cardHasOverflow()) return;
      var down = e.deltaY > 0;
      var up = e.deltaY < 0;
      if (down && !atBottom()) {
        e.preventDefault();
        card.scrollTop += e.deltaY;
      } else if (up && !atTop()) {
        e.preventDefault();
        card.scrollTop += e.deltaY;
      }
    }, { passive: false });

    /* Touch : on track la dernière position Y et on translate en delta
       sur card.scrollTop ; même règle (libère la page quand at-bottom/top). */
    var touchY = null;
    window.addEventListener('touchstart', function (e) {
      if (e.touches.length === 1) touchY = e.touches[0].clientY;
    }, { passive: true });
    window.addEventListener('touchmove', function (e) {
      if (touchY === null || e.touches.length !== 1) return;
      if (!isPinned() || !cardHasOverflow()) return;
      var current = e.touches[0].clientY;
      var dy = touchY - current;
      var down = dy > 0;
      var up = dy < 0;
      if ((down && !atBottom()) || (up && !atTop())) {
        e.preventDefault();
        card.scrollTop += dy;
        touchY = current;
      }
    }, { passive: false });
    window.addEventListener('touchend', function () { touchY = null; }, { passive: true });
  }

  /* ── Accordions : déroulement fluide + exclusivité (1 ouvert à la fois) ─
   * On anime la hauteur du <details> via la Web Animations API (WAAPI)
   * plutôt que de laisser le toggle natif s'opérer d'un coup :
   *   - Ouverture : on pose `open`, on mesure la hauteur cible, puis on
   *     anime de la hauteur fermée → ouverte (« déroulement vers le bas »).
   *   - Fermeture : on garde `open` le temps de l'animation inverse, puis
   *     on retire `open` une fois l'animation terminée — sinon le navigateur
   *     masque le contenu instantanément et il n'y a rien à animer.
   * Pourquoi pas la grid `0fr↔1fr` : la piste sommaire passait de `52px` à
   * `auto`, valeur non interpolable → la transition était ignorée (saut).
   *
   * Exclusivité : on retire l'attribut `name` (fallback natif tant que le JS
   * n'a pas tourné) et on gère la fermeture des frères en JS, pour pouvoir
   * l'ANIMER. L'exclusivité native fermerait les autres d'un coup, ce qui
   * couperait leur animation de repli. */
  function initAccordions(section) {
    var nodes = section.querySelectorAll('details.fp-v2-accordion');
    if (!nodes.length) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var DURATION = 280;
    var EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
    var instances = [];

    /* Remonte jusqu'au premier ancêtre réellement scrollable verticalement.
       Renvoie null si aucun (→ c'est la page/viewport qui scrolle, cas mobile). */
    function getScrollParent(el) {
      var p = el.parentElement;
      while (p) {
        var oy = window.getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
        p = p.parentElement;
      }
      return null;
    }

    function Accordion(el) {
      this.el = el;
      this.summary = el.querySelector('summary');
      this.panel = el.querySelector('.fp-v2-accordion__panel');
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      /* On déplace l'exclusivité native vers le JS : on mémorise le groupe
         puis on retire `name` pour neutraliser le toggle exclusif natif. */
      this.group = el.getAttribute('name');
      if (this.group) el.removeAttribute('name');

      var self = this;
      if (this.summary) {
        this.summary.addEventListener('click', function (e) { self.onClick(e); });
      }
    }

    Accordion.prototype.onClick = function (e) {
      if (!this.panel) return; // pas de panneau → laisser le natif gérer
      e.preventDefault();
      this.el.style.overflow = 'hidden';
      if (this.isClosing || !this.el.open) {
        this.open();
      } else if (this.isExpanding || this.el.open) {
        this.shrink();
      }
    };

    Accordion.prototype.open = function () {
      this.closeSiblings();
      // Fige la hauteur fermée avant de poser `open` (évite le flash plein).
      this.el.style.height = this.el.offsetHeight + 'px';
      this.el.open = true;
      var self = this;
      window.requestAnimationFrame(function () { self.expand(); });
    };

    Accordion.prototype.expand = function () {
      var self = this;
      this.isExpanding = true;
      var startHeight = this.el.offsetHeight + 'px';                       // hauteur fermée (figée)
      var endHeight = (this.el.offsetHeight + this.panel.offsetHeight) + 'px'; // + panneau déplié
      if (this.animation) this.animation.cancel();
      if (reduceMotion) { this.onAnimationFinish(true); return; }
      this.animation = this.el.animate(
        { height: [startHeight, endHeight] },
        { duration: DURATION, easing: EASING }
      );
      this.animation.onfinish = function () { self.onAnimationFinish(true); };
      this.animation.oncancel = function () { self.isExpanding = false; };
    };

    Accordion.prototype.shrink = function () {
      var self = this;
      this.isClosing = true;
      this.el.style.overflow = 'hidden';
      var startHeight = this.el.offsetHeight + 'px';
      var endHeight = (this.el.offsetHeight - this.panel.offsetHeight) + 'px'; // panneau retiré
      if (this.animation) this.animation.cancel();
      if (reduceMotion) { this.onAnimationFinish(false); return; }
      this.animation = this.el.animate(
        { height: [startHeight, endHeight] },
        { duration: DURATION, easing: EASING }
      );
      this.animation.onfinish = function () { self.onAnimationFinish(false); };
      this.animation.oncancel = function () { self.isClosing = false; };
    };

    Accordion.prototype.onAnimationFinish = function (open) {
      this.el.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.el.style.height = '';
      this.el.style.overflow = '';
      if (open) this.revealIntoView();
    };

    /* Après ouverture, si le panneau déplié dépasse sous la zone visible du
       conteneur scrollable (la card .fp-v2-main__info en desktop, ou la page
       en mobile), on fait glisser légèrement pour amener le contenu à l'écran.
       Sans ça, ouvrir le dernier accordion révèle du texte hors-champ. */
    Accordion.prototype.revealIntoView = function () {
      var MARGIN = 16;
      var rect = this.el.getBoundingClientRect();
      var container = getScrollParent(this.el);
      var behavior = reduceMotion ? 'auto' : 'smooth';
      if (container) {
        var crect = container.getBoundingClientRect();
        var delta;
        if (rect.height >= crect.height) {
          delta = rect.top - crect.top; // panneau plus grand que la card → caler son haut
        } else {
          delta = rect.bottom - crect.bottom;
          if (delta <= 0) return; // déjà entièrement visible
          delta += MARGIN;
        }
        container.scrollTo({ top: container.scrollTop + delta, behavior: behavior });
      } else {
        var vpOverflow = rect.bottom - window.innerHeight;
        if (vpOverflow <= 0) return;
        window.scrollTo({ top: window.scrollY + vpOverflow + MARGIN, behavior: behavior });
      }
    };

    Accordion.prototype.closeSiblings = function () {
      var self = this;
      instances.forEach(function (other) {
        if (other === self) return;
        if (self.group && other.group !== self.group) return;
        if (other.el.open && !other.isClosing) other.shrink();
      });
    };

    nodes.forEach(function (el) { instances.push(new Accordion(el)); });
  }

  /* ── Subscription : row "S'abonner" cliquable ──────────────────────────
   * La row entête "S'abonner" n'a pas de radio (les radios sont les pills
   * de fréquence en-dessous). Cliquer dessus active la 1ère fréquence si
   * aucune n'est encore cochée. */
  function initSubscription(section) {
    var row = section.querySelector('[data-fpv2-subscribe-row]');
    if (!row) return;
    row.style.cursor = 'pointer';
    row.addEventListener('click', function (e) {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL' || e.target.tagName === 'A')) return;
      var alreadyChecked = section.querySelector('.fp-v2-subscription__freq-input:checked');
      if (alreadyChecked) return;
      var first = section.querySelector('.fp-v2-subscription__freq-input:not(:disabled)');
      if (first) {
        first.checked = true;
        first.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ── Header height var (mode "1 écran") ───────────────────────────────
   * Mesure la hauteur réelle du header du thème (sticky ou non) et l'expose
   * comme --fpv2-header-h sur :root (cascade donc dispo pour toutes les
   * sections fp-v2-* qui en ont besoin : main, results, etc.).
   * La CSS l'utilise pour calculer `min-height: 100svh - header` et sizer
   * les cards.
   *
   * Un seul listener resize partagé pour toutes les sections (pas de leak
   * sur shopify:section:load en mode editor). */
  function syncHeaderHeightVar() {
    var header =
      document.querySelector('.site-header') ||
      document.querySelector('.mobile-nav-bar-wrapper') ||
      document.querySelector('header[role="banner"]') ||
      document.querySelector('header.section-header') ||
      document.querySelector('header');
    var h = 0;
    if (header) {
      var rect = header.getBoundingClientRect();
      h = Math.round(rect.height) || header.offsetHeight || 0;
    }
    // Barre d'annonce : section séparée AU-DESSUS du header, en flux
    // (position:relative), donc visible en haut au chargement. Elle fait partie
    // du "chrome" du haut pour le mode "1 écran". On ne l'ajoute que si elle
    // n'est pas déjà imbriquée dans le header mesuré (anti double-comptage).
    var announce = document.querySelector('.announcement_bar');
    if (announce && (!header || !header.contains(announce))) {
      var arect = announce.getBoundingClientRect();
      h += Math.round(arect.height) || announce.offsetHeight || 0;
    }
    document.documentElement.style.setProperty('--fpv2-header-h', h + 'px');
  }

  /* ── Inset du header = largeur de .fp-v2-main__inner ───────────────────
   * .fp-v2-main__inner est redimensionné au runtime (hauteur "1 écran" → sa
   * largeur finale diffère du CSS statique). On mesure sa boîte réelle et on
   * expose son inset gauche/droite (depuis le bord du viewport) sur :root,
   * pour que la pilule du header (custom.css) s'y cale exactement. Hors page
   * produit fp-v2 (ou en mobile, où le header est une barre distincte), on
   * retire les vars → le header reprend son inset par défaut (fallback CSS). */
  function syncBarInsetVar() {
    var root = document.documentElement;
    var inner = document.querySelector('.fp-v2-main__inner');
    if (!inner || !window.matchMedia('(min-width: 768px)').matches) {
      root.style.removeProperty('--fpv2-bar-inset-left');
      root.style.removeProperty('--fpv2-bar-inset-right');
      return;
    }
    // On vise les bords des CARDS (= contenu visible de l'inner), donc la
    // content-box : on retranche le padding interne (--fpv2-space-md) de la
    // border-box, pour que la pilule s'aligne pile sur les cartes (et non 24px
    // au-delà). Pour matcher la boîte de l'inner elle-même, retirer ces ± pad.
    var rect = inner.getBoundingClientRect();
    var cs = getComputedStyle(inner);
    var padL = parseFloat(cs.paddingLeft) || 0;
    var padR = parseFloat(cs.paddingRight) || 0;
    var vw = root.clientWidth; // largeur viewport sans scrollbar
    root.style.setProperty('--fpv2-bar-inset-left', Math.max(0, Math.round(rect.left + padL)) + 'px');
    root.style.setProperty('--fpv2-bar-inset-right', Math.max(0, Math.round(vw - (rect.right - padR))) + 'px');
  }

  /* ── Centrage vertical du header dans la zone vide du haut ─────────────
   * Le header est position:absolute (hors flux). .fp-v2-main est sizée
   * "1 écran" avec justify-content:end → les cards sont en bas, laissant une
   * zone vide en haut de la section. On centre la pilule du header dans la
   * bande [bas de la barre d'annonce → haut des cards (.fp-v2-main__inner)].
   *
   * On décale le conteneur absolu .header-section--wrapper via margin-top
   * (PAS un transform) pour deux raisons :
   *   1. un transform sur un ancêtre de la nav casserait le méga-menu, qui est
   *      en position:fixed (le fixed se référencerait à l'élément transformé) ;
   *   2. la marge sur le wrapper absolu ne change pas la boîte mesurée de
   *      .site-header → --fpv2-header-h reste stable → pas de boucle avec la
   *      hauteur "1 écran".
   * L'offset appliqué est mémorisé sur data-vcenter (idempotent). */
  function syncHeaderVCenter() {
    var wrapper = document.querySelector('.header-section--wrapper');
    var pill = document.querySelector('.site-header .site-header__wrapper');
    var inner = document.querySelector('.fp-v2-main__inner');
    if (!wrapper || !pill || !inner || !window.matchMedia('(min-width: 768px)').matches) {
      if (wrapper) { wrapper.style.marginTop = ''; wrapper.removeAttribute('data-vcenter'); }
      return;
    }
    // Le centrage vertical n'a de sens qu'EN HAUT de page (héro "1 écran"). Dès
    // qu'on a scrollé, .fp-v2-main__inner est remonté → bandBottom (= inner.top)
    // devient négatif et le calcul produit une marge négative aberrante qui
    // pousse le header hors de l'écran. BUG CRITIQUE : au refresh en milieu de
    // page, le header (et donc le menu) disparaissait. Hors du haut, on
    // neutralise la marge → le header reprend sa position sticky normale.
    if (window.scrollY > 2) {
      wrapper.style.marginTop = '';
      wrapper.removeAttribute('data-vcenter');
      return;
    }
    var announce = document.querySelector('.announcement_bar');
    var bandTop = announce ? announce.getBoundingClientRect().bottom : 0;
    var bandBottom = inner.getBoundingClientRect().top;
    var prev = parseFloat(wrapper.getAttribute('data-vcenter')) || 0;
    var pillRect = pill.getBoundingClientRect();          // inclut le décalage courant
    var pillCenter = pillRect.top + pillRect.height / 2;
    var newMargin = Math.round(prev + ((bandTop + bandBottom) / 2 - pillCenter));
    wrapper.style.marginTop = newMargin + 'px';
    wrapper.setAttribute('data-vcenter', newMargin);
  }

  /* Recalcule toutes les variables/positions de layout liées au header. */
  function syncFpv2Layout() {
    syncHeaderHeightVar();
    syncBarInsetVar();
    syncHeaderVCenter();
  }

  /* ── Galerie (slider "Rhode") ──────────────────────────────────────────
   * Modèle "image entrante" :
   *   - Les slides sont empilées (absolute) dans le stage. Au repos seule
   *     l'image courante (.is-active) est visible, en position galerie.
   *   - Un rail "glass" jumeau (copies basse-déf) est posé derrière la carte
   *     info translucide. Au repos ses copies sont aussi en position galerie
   *     (translateX 0) donc OCCULTÉES par la carte galerie → rien derrière le
   *     verre.
   *   - Au switch, l'image cible (galerie + glass) reçoit .is-incoming, est
   *     posée hors-champ à droite (translateX = +largeur contenu, snap sans
   *     transition) puis ramenée à translateX(0). Même variable partagée
   *     (--fpv2-incoming-x) → mouvement continu : elle arrive de la droite,
   *     traverse la zone de texte (visible floutée), puis recouvre l'image
   *     courante dans la galerie. À la fin, l'ancienne passe inactive.
   *
   * Desktop only (CSS gated min-width:750px). En mobile / sans JS / reduced
   * motion → bascule immédiate (crossfade historique via .is-active). */
  function initGallery(section) {
    var stage = section.querySelector('[data-fpv2-gallery-stage]');
    var track = section.querySelector('[data-fpv2-gallery-track]');
    if (!stage || !track) return null;

    var slides = Array.prototype.slice.call(track.querySelectorAll('.fp-v2-gallery__slide'));
    if (slides.length === 0) return null;

    var glassSlides = Array.prototype.slice.call(section.querySelectorAll('[data-fpv2-glass-track] .fp-v2-gallery__glass-slide'));
    var thumbs      = Array.prototype.slice.call(section.querySelectorAll('[data-fpv2-thumb]'));
    var galleryEl   = section.querySelector('.fp-v2-main__gallery');
    var infoEl      = section.querySelector('.fp-v2-main__info');
    // Scrollbar d'indication (mobile) — drag dimensionné/positionné au scroll.
    var scrollbar     = section.querySelector('[data-fpv2-gallery-scrollbar]');
    var scrollbarDrag = section.querySelector('[data-fpv2-gallery-scrollbar-drag]');

    var DURATION = 560;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Index courant = slide déjà .is-active (sinon 0).
    var currentIndex = 0;
    slides.forEach(function (s, i) {
      if (s.classList.contains('is-active')) currentIndex = i;
    });

    // map mediaId -> index (pour activate via variant / featured_media).
    var idToIndex = {};
    slides.forEach(function (s, i) {
      var id = s.getAttribute('data-fpv2-media-id');
      if (id != null) idToIndex[String(id)] = i;
    });

    var startX = 0;       // décalage hors-champ droit (px)
    var animTimer = null; // timer de fin d'animation
    var animTarget = -1;  // index cible de l'animation en cours

    function isDesktop() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    function measure() {
      var slideW = stage.clientWidth;
      section.style.setProperty('--fpv2-slide-w', slideW + 'px');
      // startX = pousse l'entrante au-delà du bord droit de la carte info,
      // pour qu'elle parte vraiment du hors-champ droit. Layout-agnostic.
      if (galleryEl && infoEl) {
        startX = Math.round(infoEl.getBoundingClientRect().right - galleryEl.getBoundingClientRect().left);
      } else {
        startX = slideW;
      }
    }

    function updateThumbs(i) {
      thumbs.forEach(function (t) {
        var active = parseInt(t.getAttribute('data-fpv2-target-position'), 10) === i;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // ── Scrollbar d'indication (mobile) ───────────────────────────────────
    // Dimensionne le drag proportionnellement à la fraction visible du rail et
    // le translate selon la progression du scroll horizontal. No-op desktop
    // (rail en display:contents → scrollWidth/clientWidth non pertinents) et si
    // la scrollbar n'est pas rendue (≤ 1 média).
    function updateScrollbar() {
      if (!scrollbar || !scrollbarDrag || !track) return;
      var sw = track.scrollWidth;
      var cw = track.clientWidth;
      var barW = scrollbar.clientWidth;
      if (sw <= 0 || cw <= 0 || barW <= 0) return;
      var ratio = Math.min(1, cw / sw);
      var dragW = Math.max(Math.round(barW * ratio), 24); // largeur mini lisible
      var scrollable = sw - cw;
      var progress = scrollable > 0 ? track.scrollLeft / scrollable : 0;
      var maxX = Math.max(0, barW - dragW);
      scrollbarDrag.style.width = dragW + 'px';
      scrollbarDrag.style.transform = 'translateX(' + Math.round(progress * maxX) + 'px)';
    }

    // Mobile : défile le carrousel scroll-snap vers la slide cible (clic thumb
    // résiduel / sync variante). Le swipe utilisateur, lui, met l'index à jour
    // via le listener scroll.
    function scrollToIndex(i) {
      if (!track) { swapNow(i); return; }
      currentIndex = i;
      var left = i * track.clientWidth;
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft = left;
      }
    }

    // ── Vidéos : lecture auto quand la slide devient active ───────────────
    // Le merchant peut placer une vidéo (media_type 'video') dans la galerie.
    // Quand sa slide devient active, on la lance ; les autres sont mises en
    // pause et rembobinées. Lecture tentée AVEC son d'abord (autorisée si
    // l'activation vient d'un geste : clic thumb / swipe) ; si la policy
    // autoplay du navigateur bloque, on mute et on relance (autoplay muet OK).
    function playSlideVideo(slide) {
      var v = slide && slide.querySelector('video');
      if (!v) return;
      v.playsInline = true;
      v.setAttribute('playsinline', ''); // iOS : lecture inline, pas plein écran
      var p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          v.muted = true;
          var p2 = v.play();
          if (p2 && typeof p2.catch === 'function') p2.catch(function () {});
        });
      }
    }

    function pauseSlideVideo(slide) {
      var v = slide && slide.querySelector('video');
      if (!v) return;
      v.pause();
      try { v.currentTime = 0; } catch (e) {}
    }

    function syncSlideVideos(activeIndex) {
      slides.forEach(function (s, idx) {
        if (idx === activeIndex) playSlideVideo(s);
        else pauseSlideVideo(s);
      });
    }

    // Bascule immédiate (mobile / reduced motion / no anim).
    function swapNow(i) {
      slides.forEach(function (s, idx) {
        var active = idx === i;
        s.classList.toggle('is-active', active);
        s.classList.remove('is-incoming');
        s.toggleAttribute('aria-hidden', !active);
      });
      glassSlides.forEach(function (s) { s.classList.remove('is-incoming'); });
      currentIndex = i;
      syncSlideVideos(i);
    }

    // Termine l'animation en cours : la cible devient active, l'ancienne
    // disparaît, on nettoie .is-incoming et la variable.
    function finalize() {
      if (animTimer) { clearTimeout(animTimer); animTimer = null; }
      if (animTarget < 0) return;
      var target = animTarget;
      animTarget = -1;
      swapNow(target);
      section.style.setProperty('--fpv2-incoming-x', '0px');
    }

    function setIndex(i) {
      if (i < 0 || i >= slides.length) return;
      // Toujours mettre à jour l'état des thumbs (feedback immédiat au clic).
      updateThumbs(i);

      // Mobile : carrousel scroll-snap natif → on défile vers la slide cible
      // (pas d'animation "Rhode", pas de swap d'opacité : tout est en ligne).
      if (!isDesktop()) { scrollToIndex(i); return; }

      // Si une anim est en cours, on la fige d'abord (clics rapides).
      if (animTarget >= 0) finalize();
      if (i === currentIndex) return;

      if (reduceMotion) { swapNow(i); return; }

      var gNext  = slides[i];
      var glNext = glassSlides[i];

      animTarget = i;
      gNext.setAttribute('aria-hidden', 'false');
      gNext.classList.add('is-incoming');
      if (glNext) glNext.classList.add('is-incoming');

      // 1) snap hors-champ droit (transition coupée), 2) reflow, 3) glisse à 0.
      section.classList.add('is-slide-snap');
      section.style.setProperty('--fpv2-incoming-x', startX + 'px');
      void stage.offsetWidth; // force reflow → fige l'état de départ
      section.classList.remove('is-slide-snap');
      section.style.setProperty('--fpv2-incoming-x', '0px');

      animTimer = setTimeout(finalize, DURATION + 80);
    }

    // Activation par media-id (utilisé par le variant picker). No-op si aucun
    // slide ne correspond (variante sans média dédié) → on n'efface pas tout.
    function activate(mediaId) {
      var idx = idToIndex[String(mediaId)];
      if (idx == null) return;
      setIndex(idx);
    }

    function goToThumb(thumb) {
      var pos = parseInt(thumb.getAttribute('data-fpv2-target-position'), 10);
      if (!isNaN(pos)) { setIndex(pos); return; }
      var mediaId = thumb.getAttribute('data-fpv2-target-media-id');
      if (mediaId) activate(mediaId);
    }

    // Survol = slide vers l'image (comme Rhode), réservé aux pointeurs précis
    // (souris/trackpad) pour ne pas casser le tap sur tactile. Petit délai
    // anti-balayage : un passage rapide sur plusieurs vignettes ne déclenche
    // que celle où le curseur se pose vraiment.
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var hoverTimer = null;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { goToThumb(thumb); });

      if (canHover) {
        thumb.addEventListener('mouseenter', function () {
          if (hoverTimer) clearTimeout(hoverTimer);
          hoverTimer = setTimeout(function () { goToThumb(thumb); }, 70);
        });
        thumb.addEventListener('mouseleave', function () {
          if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
        });
      }
    });

    // Bascule en mode slider (CSS) puis mesure dans le même tick (pas de flash).
    section.classList.add('is-slider-ready');
    measure();

    // Slide active au chargement : si elle contient une vidéo, on la lance
    // (autoplay muet, faute de geste utilisateur à ce stade).
    syncSlideVideos(currentIndex);

    // ── Mobile : carrousel swipeable (scroll-snap) ────────────────────────
    // Le swipe défile le rail nativement ; on suit le scroll pour (1) redessiner
    // la scrollbar d'indication, (2) tenir currentIndex/thumbs à jour. Listener
    // inerte en desktop (rail en display:contents → ne génère pas de scroll).
    var scrollRaf = false;
    function onTrackScroll() {
      if (scrollRaf) return;
      scrollRaf = true;
      window.requestAnimationFrame(function () {
        scrollRaf = false;
        updateScrollbar();
        var cw = track.clientWidth;
        if (cw <= 0) return;
        var idx = Math.round(track.scrollLeft / cw);
        if (idx >= 0 && idx < slides.length && idx !== currentIndex) {
          currentIndex = idx;
          updateThumbs(idx);
          syncSlideVideos(idx); // mobile : la vidéo amenée en vue se lance
        }
      });
    }
    track.addEventListener('scroll', onTrackScroll, { passive: true });

    // État initial mobile : expose toutes les slides aux lecteurs d'écran (le
    // crossfade desktop les masque via aria-hidden), cale le rail sur la slide
    // active et dessine la scrollbar.
    if (!isDesktop()) {
      slides.forEach(function (s) { s.removeAttribute('aria-hidden'); });
      if (currentIndex > 0) track.scrollLeft = currentIndex * track.clientWidth;
      updateScrollbar();
    }

    // Re-mesure : resize (debounced) + ResizeObserver sur les cartes (la
    // hauteur "1 écran" / les fonts peuvent décaler les largeurs après coup).
    var rT;
    window.addEventListener('resize', function () {
      if (rT) clearTimeout(rT);
      rT = setTimeout(function () { measure(); updateScrollbar(); }, 120);
    }, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () { measure(); updateScrollbar(); });
      if (galleryEl) ro.observe(galleryEl);
      if (infoEl) ro.observe(infoEl);
    }

    return { activate: activate };
  }

  /* ── Thumbnails : défilement (style Rhode) ─────────────────────────────
   * La liste de vignettes n'affiche que 4 vignettes (max-height CSS) ; le
   * surplus scrolle verticalement. Deux flèches (rendues par Liquid seulement
   * si > 4 médias) font défiler d'une "page" à chaque clic :
   *   - flèche HAUT  : masquée quand on est tout en haut, visible dès qu'on
   *                    peut remonter.
   *   - flèche BAS   : masquée quand on est tout en bas, visible dès qu'on
   *                    peut descendre.
   * Le masquage se fait via l'attribut `hidden` (CSS : display:none).
   *
   * En parallèle on pose des classes de fade sur le wrapper (is-fade-top /
   * is-fade-bottom) selon la position de scroll : la 1re / dernière vignette
   * visible s'estompe tant qu'il reste du contenu à révéler dans ce sens.
   *
   * Progressive enhancement : sans JS, la liste reste scrollable nativement
   * (overflow:auto) ; seuls les boutons-flèches sont inertes. */
  function initThumbScroll(section) {
    var wrap = section.querySelector('[data-fpv2-thumbs-wrap]');
    if (!wrap) return;
    var list = wrap.querySelector('[data-fpv2-thumbs-list]');
    if (!list) return;
    var upArrow   = wrap.querySelector('[data-fpv2-thumbs-up]');
    var downArrow = wrap.querySelector('[data-fpv2-thumbs-down]');

    function maxScroll() {
      return list.scrollHeight - list.clientHeight;
    }

    function updateState() {
      var max = maxScroll();
      var hasOverflow = max > 1;
      var atTop = list.scrollTop <= 1;
      var atBottom = list.scrollTop >= max - 1;
      wrap.classList.toggle('is-fade-top', hasOverflow && !atTop);
      wrap.classList.toggle('is-fade-bottom', hasOverflow && !atBottom);
      // On peut remonter ? → flèche haut visible. Descendre ? → flèche bas.
      if (upArrow)   upArrow.hidden   = !(hasOverflow && !atTop);
      if (downArrow) downArrow.hidden = !(hasOverflow && !atBottom);
    }

    // Défile d'une "page" ≈ hauteur visible de la liste (4 vignettes).
    function scrollByPage(dir) {
      list.scrollTo({ top: list.scrollTop + dir * list.clientHeight, behavior: 'smooth' });
    }
    if (upArrow)   upArrow.addEventListener('click', function () { scrollByPage(-1); });
    if (downArrow) downArrow.addEventListener('click', function () { scrollByPage(1); });

    var rafPending = false;
    list.addEventListener('scroll', function () {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(function () {
        rafPending = false;
        updateState();
      });
    }, { passive: true });

    // L'overflow de la liste dépend de la hauteur des vignettes : on recalcule
    // après le chargement des images et sur tout changement de taille, sinon
    // les flèches peuvent rester figées dans l'état mesuré "à vide".
    window.addEventListener('load', updateState);
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(updateState).observe(list);
    }

    updateState();
  }

  /* ── Variant picker ────────────────────────────────────────────────── */
  function initVariantPicker(section, sectionId, gallery) {
    var variantsJsonEl = section.querySelector('[data-fpv2-variants-json]');
    if (!variantsJsonEl) return;

    var variants;
    try {
      variants = JSON.parse(variantsJsonEl.textContent);
    } catch (err) {
      console.warn('[fp-v2-main] variants JSON parse failed:', err);
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    var optionInputs = section.querySelectorAll('[data-fpv2-option-input]');
    var hiddenIdInput = section.querySelector('[data-fpv2-variant-id-input]');
    var priceEl       = section.querySelector('[data-fpv2-price]');
    var compareEl     = section.querySelector('[data-fpv2-price-compare]');
    var stockEl       = section.querySelector('[data-fpv2-stock]');
    var atcBtn        = section.querySelector('[data-fpv2-atc]');
    var atcText       = section.querySelector('[data-fpv2-atc-text]');

    // Prix rendus HORS du bloc `price` : le CTA et le bloc abonnement. Liquid
    // ne les rend que pour la variante initiale — sans ces refs, changer de
    // variante ne les touchait pas (et sur les fiches sans bloc `price`,
    // AUCUN prix ne bougeait).
    var atcPriceEl    = section.querySelector('[data-fpv2-atc-price]');
    var subOneTimeEl  = section.querySelector('[data-fpv2-sub-onetime-price]');

    // Prix barrés. Le bloc `price` n'est pas posé sur les fiches 2026 : le
    // `compare_at_price` s'affiche alors dans la pastille "achat unique" et
    // dans le CTA, qui portent chacun leur propre <s>.
    var subOneTimeCmp = section.querySelector('[data-fpv2-sub-onetime-compare]');
    var atcCmpEl      = section.querySelector('[data-fpv2-atc-compare]');
    var subPriceEl    = section.querySelector('[data-fpv2-sub-price]');
    var subNoneInput  = section.querySelector('[data-fpv2-selling-plan-none]');
    var badgeSaleEl   = section.querySelector('[data-fpv2-badge-sale]');
    // Rendu par Liquid uniquement si le PRODUIT entier est indisponible ; dans
    // ce cas il prime sur le badge promo, qui reste masqué.
    var badgeSoldOut  = section.querySelector('[data-fpv2-badge-soldout]');
    var freqItems     = section.querySelectorAll('[data-fpv2-freq-item]');
    var planInputs    = section.querySelectorAll('[data-fpv2-selling-plan]');

    var currentVariant = null;

    // Table { variantId: { planId: perDeliveryPrice } } rendue par le bloc
    // subscription. Absente = pas d'abonnement sur cette fiche (ou Liquid n'a
    // rien à exposer) : on laisse alors le bloc tel quel plutôt que de le
    // vider à tort.
    var subAllocs = null;
    var subAllocsEl = section.querySelector('[data-fpv2-sub-allocations]');
    if (subAllocsEl) {
      try {
        subAllocs = JSON.parse(subAllocsEl.textContent);
      } catch (err) {
        console.warn('[fp-v2-main] subscription allocations JSON parse failed:', err);
      }
    }

    function readSelectedOptions() {
      var selected = [];
      optionInputs.forEach(function (input) {
        if (input.checked) {
          var idx = parseInt(input.getAttribute('data-fpv2-option-index'), 10);
          selected[idx] = input.value;
        }
      });
      return selected;
    }

    function findVariant(selectedOptions) {
      return variants.find(function (v) {
        return v.options && v.options.every(function (val, i) {
          return val === selectedOptions[i];
        });
      });
    }

    /* Variante courante d'après le DOM. Sans sélecteur d'options (fiche
       mono-variante), on retombe sur l'input caché `id` du formulaire. */
    function readCurrentVariant() {
      var found = findVariant(readSelectedOptions());
      if (found) return found;
      if (hiddenIdInput) {
        var id = String(hiddenIdInput.value);
        return variants.find(function (v) { return String(v.id) === id; }) || null;
      }
      return null;
    }

    /* Barré générique : on ne l'affiche que s'il dépasse le prix RÉELLEMENT
       affiché à côté (achat unique ou prix abonné), jamais un montant
       inférieur qui se lirait comme une hausse. */
    function setCompare(el, variant, shownPrice) {
      if (!el) return;
      var compareAt = variant && variant.compare_at_price;
      var onSale = compareAt && compareAt > shownPrice;
      el.textContent = onSale ? formatMoney(compareAt) : '';
      el.hidden = !onSale;
    }

    /* Badge promo : `compare_at_price` est propre à la variante, le badge doit
       donc apparaître / disparaître avec elle. */
    function updateSaleBadge(variant) {
      if (!badgeSaleEl || !variant) return;
      var onSale = !badgeSoldOut
                && variant.compare_at_price
                && variant.compare_at_price > variant.price;
      badgeSaleEl.hidden = !onSale;
    }

    /* Prix par livraison d'un selling plan pour une variante donnée, ou null
       si cette variante ne propose pas ce plan. */
    function planPrice(variant, planId) {
      if (!subAllocs || !variant || !planId) return null;
      var forVariant = subAllocs[String(variant.id)];
      if (!forVariant) return null;
      var price = forVariant[String(planId)];
      return typeof price === 'number' ? price : null;
    }

    function checkedPlanId() {
      var checked = section.querySelector('[data-fpv2-selling-plan]:checked');
      return checked && checked.value ? checked.value : '';
    }

    /* Bloc abonnement : prix achat unique + prix abonné (celui de la fréquence
       cochée, sinon la première disponible). Les fréquences que la nouvelle
       variante ne propose pas sont masquées ET désactivées, pour ne jamais
       soumettre un selling_plan invalide. */
    function updateSubscription(variant) {
      if (!variant) return;

      if (subOneTimeEl) subOneTimeEl.textContent = formatMoney(variant.price);
      setCompare(subOneTimeCmp, variant, variant.price);
      if (!subAllocs) return;

      var firstAvailable = null;
      var lostChecked = false;

      freqItems.forEach(function (item) {
        var input = item.querySelector('[data-fpv2-selling-plan]');
        if (!input) return;
        var available = planPrice(variant, input.value) !== null;
        item.hidden = !available;
        input.disabled = !available;
        if (available) {
          if (!firstAvailable) firstAvailable = input;
        } else if (input.checked) {
          input.checked = false;
          lostChecked = true;
        }
      });

      if (lostChecked) {
        if (firstAvailable) firstAvailable.checked = true;
        else if (subNoneInput) subNoneInput.checked = true;
      }

      if (subPriceEl) {
        var planId = checkedPlanId() || (firstAvailable ? firstAvailable.value : '');
        var price = planPrice(variant, planId);
        if (price !== null) subPriceEl.textContent = formatMoney(price);
      }
    }

    /* Prix intégré au CTA : suit la variante, et le plan d'abonnement coché
       le cas échéant (c'est ce montant-là qui part au panier). */
    function updateAtcPrice(variant) {
      if (!atcPriceEl || !variant) return;
      var price = planPrice(variant, checkedPlanId());
      var shown = price !== null ? price : variant.price;
      atcPriceEl.textContent = formatMoney(shown);
      setCompare(atcCmpEl, variant, shown);
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (e) { /* noop */ }
    }

    function updateUI(variant) {
      if (!variant) {
        if (atcBtn) atcBtn.disabled = true;
        if (atcText && atcText.dataset.fpv2Unavailable) {
          atcText.textContent = atcText.dataset.fpv2Unavailable;
        }
        return;
      }

      currentVariant = variant;

      if (hiddenIdInput) hiddenIdInput.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      // Ordre important : updateSubscription peut recocher une fréquence
      // (plan absent de la nouvelle variante), et le prix du CTA en dépend.
      updateSubscription(variant);
      updateAtcPrice(variant);
      updateSaleBadge(variant);

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.innerHTML = '<s>' + formatMoney(variant.compare_at_price) + '</s>';
          compareEl.hidden = false;
        } else {
          compareEl.innerHTML = '';
          compareEl.hidden = true;
        }
      }

      if (atcBtn) {
        atcBtn.disabled = !variant.available;
      }
      if (stockEl) {
        stockEl.classList.toggle('fp-v2-stock--out', !variant.available);
        stockEl.classList.toggle('fp-v2-stock--in',  variant.available);
      }

      // Affiche l'image associée à la variante (si elle en a une dédiée).
      if (gallery && variant.featured_media && variant.featured_media.id != null) {
        gallery.activate(variant.featured_media.id);
      }

      updateUrl(variant);

      // Notify the rest of the theme (cart drawer, recommendations, etc.)
      if (window.eventBus && typeof window.eventBus.emit === 'function') {
        window.eventBus.emit('fpv2:variant:change', {
          sectionId: sectionId,
          variant: variant
        });
        // Compatibility with theme-wide events some themes listen for
        window.eventBus.emit('variant:change', {
          sectionId: sectionId,
          variant: variant
        });
      }
    }

    function onChange() {
      var selected = readSelectedOptions();
      var variant = findVariant(selected);
      updateUI(variant);
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', onChange);
    });

    // Changer de fréquence (ou revenir à l'achat unique) change le prix
    // facturé : le CTA et la ligne "s'abonner" doivent suivre.
    planInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        updateSubscription(currentVariant);
        updateAtcPrice(currentVariant);
      });
    });

    // État initial : Liquid a rendu les prix de la variante d'arrivée, mais
    // pas forcément les bonnes fréquences disponibles.
    currentVariant = readCurrentVariant();
    if (currentVariant) {
      updateSubscription(currentVariant);
      updateAtcPrice(currentVariant);
      updateSaleBadge(currentVariant);
    }
  }

  /* ── Quantity ──────────────────────────────────────────────────────── */
  function initQuantity(section) {
    var input = section.querySelector('[data-fpv2-qty-input]');
    var dec   = section.querySelector('[data-fpv2-qty-decrease]');
    var inc   = section.querySelector('[data-fpv2-qty-increase]');
    if (!input) return;

    function clamp(val) {
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10);
      if (isNaN(val) || val < min) val = min;
      if (!isNaN(max) && val > max) val = max;
      return val;
    }

    if (dec) dec.addEventListener('click', function () {
      input.value = clamp(parseInt(input.value, 10) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    if (inc) inc.addEventListener('click', function () {
      input.value = clamp(parseInt(input.value, 10) + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('change', function () {
      input.value = clamp(parseInt(input.value, 10));
    });
  }

  /* ── Bootstrap ─────────────────────────────────────────────────────── */
  function bootstrap() {
    document.querySelectorAll('[data-section-type="fp-v2-main"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Theme editor : re-init on section load
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-section-type="fp-v2-main"]');
    if (section) initSection(section);
  });

  /* Listener resize unique (au niveau module, pas par section) pour
     re-mesurer le header height — debounced. */
  var resizeTimer;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(syncFpv2Layout, 100);
  }, { passive: true });

  /* Le centrage vertical du header dépend de la position de scroll (il ne vaut
     qu'en haut de page). On le ré-évalue au scroll (throttlé rAF) : neutralisé
     dès qu'on quitte le haut, restauré au retour. Sans ça, une marge calculée
     pour le haut reste figée quand on scrolle → header décalé / hors écran. */
  var vcenterScrollRaf = false;
  window.addEventListener('scroll', function () {
    if (vcenterScrollRaf) return;
    vcenterScrollRaf = true;
    window.requestAnimationFrame(function () {
      vcenterScrollRaf = false;
      syncHeaderVCenter();
    });
  }, { passive: true });

  // Re-sync après chargement des fonts (peut décaler logo / nav du header).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(syncFpv2Layout).catch(function () {});
  }
  window.addEventListener('load', syncFpv2Layout);

  /* ResizeObserver sur le header : capture toute variation (fonts, sticky
     state, responsive breakpoint, annonce bar qui apparaît) bien avant que
     fonts.ready / window.load ne soient déclenchés — supprime le FOUC
     "card trop haute" pendant 4-5s au chargement de pages lourdes. */
  function installHeaderObserver() {
    if (typeof ResizeObserver === 'undefined') return;
    var header =
      document.querySelector('.site-header') ||
      document.querySelector('.mobile-nav-bar-wrapper') ||
      document.querySelector('header[role="banner"]') ||
      document.querySelector('header.section-header') ||
      document.querySelector('header');
    var announce = document.querySelector('.announcement_bar');
    var inner = document.querySelector('.fp-v2-main__inner');
    if (!header && !announce && !inner) return;
    var ro = new ResizeObserver(syncHeaderHeightVar);
    if (header) ro.observe(header);
    // Observe aussi la barre d'annonce (apparition/disparition, hauteur
    // responsive) pour recalculer --fpv2-header-h immédiatement.
    if (announce) ro.observe(announce);
    // Observer dédié à l'inner fp-v2 : sa taille/position change au runtime
    // (hauteur "1 écran") → recale l'inset horizontal ET le centrage vertical
    // du header. Séparé du header pour éviter une boucle (le header change de
    // largeur quand on applique l'inset ; le transform vertical n'impacte pas
    // la mesure de --fpv2-header-h).
    if (inner) new ResizeObserver(function () {
      syncBarInsetVar();
      syncHeaderVCenter();
    }).observe(inner);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installHeaderObserver);
  } else {
    installHeaderObserver();
  }
})();

/* ── Note (★★★★★ (n)) recalée sur la liste d'avis ───────────────────────────
 * Le Liquid rend le bloc depuis les metafields Yotpo, qui décrochent. La liste
 * de fp-v2-reviews (API Yotpo, ce que le client lit réellement) publie son
 * bottomline : on y aligne étoiles, nombre, aria-label et microdata, et on
 * masque le bloc si la liste confirme zéro avis. Sans section avis sur la
 * page, rien n'est publié et le rendu serveur reste tel quel.
 * ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* Même arrondi que le Liquid : quart d'étoile inférieur (5 pleines = 5,0 pile). */
  function quarterFloor(avg) {
    return Math.min(20, Math.floor(avg * 4)) / 4;
  }

  function apply(bl) {
    if (!bl || !bl.productId) return;
    var display = (Math.round(bl.average * 10) / 10).toFixed(1);
    document.querySelectorAll('[data-fpv2-rating][data-product-id="' + bl.productId + '"]').forEach(function (link) {
      if (!bl.total) {
        link.hidden = true;
        return;
      }
      var starsEl = link.querySelector('[data-fpv2-stars]');
      var countEl = link.querySelector('[data-fpv2-rating-count]');
      if (starsEl) starsEl.style.setProperty('--fpv2-stars-value', String(quarterFloor(bl.average)));
      if (countEl) countEl.textContent = '(' + bl.total + ')';
      var tpl = link.getAttribute('data-aria-tpl');
      if (tpl) link.setAttribute('aria-label', tpl.split('[[r]]').join(display).split('[[c]]').join(String(bl.total)));
      link.hidden = false;
    });
    document.querySelectorAll('[data-fpv2-rating-microdata="' + bl.productId + '"]').forEach(function (md) {
      if (!bl.total) { md.remove(); return; }
      var rv = md.querySelector('[itemprop="ratingValue"]');
      var rc = md.querySelector('[itemprop="reviewCount"]');
      if (rv) rv.setAttribute('content', display);
      if (rc) rc.setAttribute('content', String(bl.total));
    });
  }

  function applyCached() {
    var cache = window.fpv2ReviewsBottomline;
    if (cache) Object.keys(cache).forEach(function (id) { apply(cache[id]); });
  }

  document.addEventListener('fpv2:reviews:bottomline', function (e) { apply(e.detail); });
  /* La liste a pu répondre avant ce script, ou la section main être rechargée
     dans l'éditeur de thème : on rejoue le dernier bottomline connu. */
  applyCached();
  document.addEventListener('shopify:section:load', applyCached);
})();
